/**
 * ApprovalQueue — human-approval workflow panel.
 * Shows pending changes with before/after diff. Approve / reject / apply to Shopify.
 */
import React, { useState, useEffect, useCallback } from "react";

const STATUS_BADGE = {
  pending:  "seo-status-pending",
  approved: "seo-status-approved",
  rejected: "seo-status-rejected",
  applied:  "seo-status-applied",
  failed:   "seo-status-failed",
};

const TYPE_LABELS = {
  product_title:       "Title",
  product_description: "Description",
  product_tags:        "Tags",
  product_handle:      "URL Handle",
  image_alt:           "Image Alt",
  collection_title:    "Collection Title",
  blog_post:           "Blog Post",
  meta_field:          "Meta Field",
};

function ChangeCard({ change, onApprove, onReject, onApply, applying }) {
  const [expanded, setExpanded] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const isHtml = change.change_type === "product_description";

  return (
    <div className={`seo-change-card ${change.compliance_safe ? "" : "seo-change-card-unsafe"}`}>
      <div className="seo-change-header" onClick={() => setExpanded(e => !e)}>
        <div className="seo-change-header-left">
          <span className={`seo-status-badge ${STATUS_BADGE[change.status] || ""}`}>
            {change.status}
          </span>
          <span className="seo-change-type-badge">
            {TYPE_LABELS[change.change_type] || change.change_type}
          </span>
          {!change.compliance_safe && (
            <span className="seo-compliance-warn">⚠️ Review</span>
          )}
        </div>
        <div className="seo-change-header-right">
          <span className="seo-change-product">{change.target_title}</span>
          <span className="seo-change-chevron">{expanded ? "▲" : "▼"}</span>
        </div>
      </div>

      {expanded && (
        <div className="seo-change-body">
          {change.notes && (
            <div className="seo-change-notes">{change.notes}</div>
          )}
          <div className="seo-diff-grid">
            <div className="seo-diff-col seo-diff-before">
              <div className="seo-diff-col-label">Current</div>
              {isHtml ? (
                <div
                  className="seo-diff-html"
                  dangerouslySetInnerHTML={{ __html: change.current_value || "<em>(empty)</em>" }}
                />
              ) : (
                <div className="seo-diff-text">{change.current_value || "(empty)"}</div>
              )}
            </div>
            <div className="seo-diff-col seo-diff-after">
              <div className="seo-diff-col-label">Proposed</div>
              {isHtml ? (
                <div
                  className="seo-diff-html"
                  dangerouslySetInnerHTML={{ __html: change.proposed_value || "<em>(empty)</em>" }}
                />
              ) : (
                <div className="seo-diff-text seo-diff-text-new">{change.proposed_value || "(empty)"}</div>
              )}
            </div>
          </div>

          {rejecting && (
            <div className="seo-reject-form">
              <input
                className="seo-input"
                placeholder="Rejection reason (optional)"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  className="seo-btn seo-btn-danger seo-btn-sm"
                  onClick={() => { onReject(change.id, rejectReason); setRejecting(false); }}
                >
                  Confirm Reject
                </button>
                <button className="seo-btn seo-btn-ghost seo-btn-sm" onClick={() => setRejecting(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {change.status === "pending" && !rejecting && (
            <div className="seo-change-actions">
              <button
                className="seo-btn seo-btn-primary seo-btn-sm"
                onClick={() => onApprove(change.id)}
              >
                ✓ Approve
              </button>
              <button
                className="seo-btn seo-btn-danger seo-btn-sm"
                onClick={() => setRejecting(true)}
              >
                ✕ Reject
              </button>
            </div>
          )}

          {change.status === "approved" && (
            <div className="seo-change-actions">
              <button
                className="seo-btn seo-btn-green seo-btn-sm"
                onClick={() => onApply(change.id)}
                disabled={applying}
              >
                {applying ? "Applying…" : "🚀 Apply to Shopify"}
              </button>
            </div>
          )}

          {change.status === "applied" && (
            <div className="seo-change-applied">✅ Applied to Shopify</div>
          )}
          {change.status === "failed" && (
            <div className="seo-change-failed">❌ Failed: {change.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApprovalQueue({ onStatsChange }) {
  const [changes, setChanges]   = useState([]);
  const [stats, setStats]       = useState({});
  const [loading, setLoading]   = useState(false);
  const [applying, setApplying] = useState(null);
  const [filter, setFilter]     = useState("pending");
  const [result, setResult]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/seo/approval/queue");
      const d = await r.json();
      if (d.success) {
        setChanges(d.changes || []);
        setStats(d.stats || {});
        if (onStatsChange) onStatsChange(d.stats);
      }
    } catch (e) {
      console.error("Queue load failed", e);
    } finally {
      setLoading(false);
    }
  }, [onStatsChange]);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await fetch("/api/seo/approval/approve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ change_ids: [id] }),
      });
      load();
    } catch (e) { console.error(e); }
  };

  const approveAllSafe = async () => {
    try {
      const r = await fetch("/api/seo/approval/approve-all-safe", { method: "POST" });
      const d = await r.json();
      setResult(`Approved ${d.approved?.length || 0} safe changes`);
      load();
    } catch (e) { setResult("Failed to approve"); }
  };

  const reject = async (id, reason) => {
    try {
      await fetch("/api/seo/approval/reject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ change_id: id, reason }),
      });
      load();
    } catch (e) { console.error(e); }
  };

  const apply = async (id) => {
    setApplying(id);
    try {
      const r = await fetch("/api/seo/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ change_ids: [id] }),
      });
      const d = await r.json();
      const res = d.results?.[0];
      setResult(res?.status === "applied" ? "✅ Applied to Shopify" : `Failed: ${res?.status}`);
      load();
    } catch (e) {
      setResult("Apply failed");
    } finally {
      setApplying(null);
    }
  };

  const applyAllApproved = async () => {
    const approvedIds = changes
      .filter(c => c.status === "approved")
      .map(c => c.id);
    if (!approvedIds.length) { setResult("No approved changes to apply"); return; }
    setApplying("all");
    try {
      const r = await fetch("/api/seo/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ change_ids: approvedIds }),
      });
      const d = await r.json();
      const applied = d.results?.filter(r => r.status === "applied").length || 0;
      setResult(`Applied ${applied} / ${approvedIds.length} changes to Shopify`);
      load();
    } catch (e) {
      setResult("Apply all failed");
    } finally {
      setApplying(null);
    }
  };

  const displayed = changes.filter(c => filter === "all" || c.status === filter);

  return (
    <div className="seo-queue-wrap">
      {/* Stats bar */}
      <div className="seo-queue-stats">
        {[
          { k: "pending",  label: "Pending",  cls: "seo-text-warn" },
          { k: "approved", label: "Approved", cls: "seo-text-green" },
          { k: "applied",  label: "Applied",  cls: "seo-text-blue" },
          { k: "rejected", label: "Rejected", cls: "seo-text-red" },
          { k: "compliance_unsafe", label: "Need Review", cls: "seo-text-orange" },
        ].map(({ k, label, cls }) => (
          <div key={k} className="seo-queue-stat">
            <span className={`seo-queue-stat-num ${cls}`}>{stats[k] || 0}</span>
            <span className="seo-queue-stat-lbl">{label}</span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="seo-queue-actions">
        <div className="seo-filter-tabs">
          {["pending", "approved", "applied", "all"].map(f => (
            <button
              key={f}
              className={`seo-filter-tab ${filter === f ? "active" : ""}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === "pending" && stats.pending ? ` (${stats.pending})` : ""}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="seo-btn seo-btn-ghost seo-btn-sm" onClick={load} disabled={loading}>
            {loading ? "⟳" : "↺ Refresh"}
          </button>
          {filter === "pending" && (
            <button className="seo-btn seo-btn-primary seo-btn-sm" onClick={approveAllSafe}>
              ✓ Approve All Safe
            </button>
          )}
          {filter === "approved" && (
            <button
              className="seo-btn seo-btn-green seo-btn-sm"
              onClick={applyAllApproved}
              disabled={applying === "all"}
            >
              {applying === "all" ? "Applying…" : "🚀 Apply All to Shopify"}
            </button>
          )}
        </div>
      </div>

      {result && (
        <div className="seo-toast seo-toast-ok">
          {result}
          <button className="seo-toast-close" onClick={() => setResult(null)}>✕</button>
        </div>
      )}

      {/* Change list */}
      <div className="seo-change-list">
        {loading && <div className="seo-loading">Loading queue…</div>}
        {!loading && displayed.length === 0 && (
          <div className="seo-empty">
            {filter === "pending"
              ? "No pending changes — generate SEO to create some."
              : `No ${filter} changes.`}
          </div>
        )}
        {displayed.map(c => (
          <ChangeCard
            key={c.id}
            change={c}
            onApprove={approve}
            onReject={reject}
            onApply={apply}
            applying={applying === c.id}
          />
        ))}
      </div>
    </div>
  );
}
